import { useState, useRef, useEffect } from 'react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { ChevronDown, Check } from 'lucide-react';

export function CurrencySwitcher({ compact = false }) {
  const { currencyCode, symbol, currencies, setCurrency, currencyLoaded } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!currencyLoaded || !currencies?.length) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="currency-switcher-btn"
        className="flex items-center gap-1.5 rounded-full transition-colors active:scale-95"
        style={{
          background: open ? 'var(--brand-primary-light)' : 'transparent',
          padding: compact ? '6px 10px' : '8px 12px',
          fontSize: compact ? '12px' : '13px',
          color: 'var(--text-main)',
          fontWeight: 500,
        }}
        aria-label="Change currency"
      >
        <span style={{ fontWeight: 600 }}>{symbol}</span>
        <span>{currencyCode}</span>
        <ChevronDown size={compact ? 12 : 14} style={{ color: 'var(--text-muted)' }} />
      </button>

      {open && (
        <div
          data-testid="currency-switcher-menu"
          className="absolute right-0 mt-2 nsofar-card shadow-lg overflow-hidden z-50 animate-fade-in-up"
          style={{ minWidth: 200 }}
        >
          <div className="p-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <p className="text-xs uppercase tracking-wider px-2 py-1" style={{ color: 'var(--text-muted)' }}>
              Currency
            </p>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {currencies.map((c) => {
              const selected = c.currency_code === currencyCode;
              return (
                <button
                  key={c.currency_code}
                  data-testid={`currency-option-${c.currency_code}`}
                  onClick={() => { setCurrency(c.currency_code); setOpen(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[var(--hover-surface)] transition-colors"
                  style={{ color: 'var(--text-main)' }}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-semibold" style={{ minWidth: 24 }}>{c.symbol}</span>
                    <span>{c.currency_code}</span>
                  </span>
                  {selected && <Check size={14} style={{ color: 'var(--brand-primary)' }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
